import './style.css';
import {
  Link,
  Outlet,
  useLocation,
  useNavigate
} from 'react-router-dom';
import {
  useEffect,
  useRef,
  useState
} from 'react';
import routes from './routes';
import { i18n } from '@lingui/core';
import { I18nProvider } from '@lingui/react';
import { msg } from '@lingui/core/macro';
import Dashboard from './dashboard';
import Navigation from 'components/navigation';
import useDevicesStore from 'store/devices';
import useDeviceTypesStore from 'store/device-types';
import useAlarmsStore from 'store/alarms';
import useUsersStore from 'store/users';
import useRolesStore from 'store/roles';
import useLicenseStore from 'store/license';
import useLocalStore from 'store/local';
import {
  createModal,
  GlobalBanner,
  useNotificationsStore
} from '@thermon-platforms/thermon-ui-core';
import Cookies from 'js-cookie';
import Login from './login';
import LicenseExpirationModal from './settings/license/expiration-modal';


/*
 * Application root
 */
function App() {
  const navigate = useNavigate();
  const location = useLocation();
  const [devicesStore, devicesStoreActions] = useDevicesStore();
  const [, deviceTypesStoreActions] = useDeviceTypesStore();
  const [alarmsStore, alarmsStoreActions] = useAlarmsStore();
  const [, usersStoreActions] = useUsersStore();
  const [, rolesStoreActions] = useRolesStore();
  const [localStore, localStoreActions] = useLocalStore();
  const [licenseStore, licenseStoreActions] = useLicenseStore();
  const [notificationsStore, notificationsStoreActions] = useNotificationsStore();
  const [authenticated, setAuthenticated] = useState(!!Cookies.get('AuthToken'));

  useEffect(() => {
    // If the session has expired, let's redirect to login
    if (!authenticated) {
      if (location.pathname == '/' || location.pathname == '/login') {
        navigate(`/login`);
      } else {
        navigate(`/login?return_to=${location.pathname}`);
      }
    }
  }, []);

  // Check if we're logged in
  useEffect(() => {
    setAuthenticated(!!Cookies.get('AuthToken'));
  }, [location]);

  // Pre-load core assets
  const alarmsInterval = useRef();
  const licenseInterval = useRef();
  useEffect(() => {
    if (!authenticated) return;
    notificationsStoreActions.clearGlobalNotification();
    devicesStoreActions.query();
    deviceTypesStoreActions.query();
    usersStoreActions.query();
    rolesStoreActions.query();
    // Query alarms immediately
    alarmsStoreActions.query();
    // And, grab them again every minute
    alarmsInterval.current = setInterval(() => {
      if (document.visibilityState != 'visible') return;
      alarmsStoreActions.query();
    }, 60 * 1000);
    // Query the license immediately
    licenseStoreActions.query();
    // And, grab it again once a day
    licenseInterval.current = setInterval(() => {
      if (document.visibilityState != 'visible') return;
      licenseStoreActions.query();
    }, 24 * 60 * 60 * 1000);
    return () => {
      if (alarmsInterval.current) clearInterval(alarmsInterval.current);
      if (licenseInterval.current) clearInterval(licenseInterval.current);
    }
  }, [authenticated]);

  // If one of our core resources errs, let's elevate the error
  useEffect(() => {
    if (devicesStore.error || alarmsStore.error) {
      notificationsStoreActions.setGlobalNotification(
        i18n._(msg`There was an issue communicating with the API.`),
        'warning'
      )
    }

    if (devicesStore.updated && devicesStore.devices.length == 0  && !devicesStore.error) {
      // If devices load and is empty, navigate to get-started
      // We should maybe block the UI until devices are loaded
      // so it doesn't flash from one view to another.
      navigate('get-started');
    }
  }, [devicesStore, alarmsStore]);

  // Reusable method for spawning the license expiration modal
  const licenseTimeout = useRef(null);
  function openLicenseExpirationModal() {
    createModal(
      <LicenseExpirationModal expiration={licenseStore.expiration} />
    )
      .open()
      .then(() => {
        // Clear any existing timeout before setting a new one
        if (licenseTimeout.current) {
          clearTimeout(licenseTimeout.current);
        }

        // Handle the license expiration modal re-opening
        if (licenseStore.expiration <= 30 && licenseStore.expiration > 5) {
          licenseTimeout.current = setTimeout(() => {
            openLicenseExpirationModal();
          }, 24 * 60 * 60 * 1000); // Reopen every 24 hours
        } else if (licenseStore.expiration <= 5 && licenseStore.expiration > 0) {
          licenseTimeout.current = setTimeout(() => {
            openLicenseExpirationModal();
          }, 1 * 60 * 60 * 1000); // Reopen every hour
        }
      });
  }

  // Handle license side-effects
  useEffect(() => {
    if (!devicesInitialLoad.current) return;
    // If there are no devices, we can assume this is a fresh install
    if (devicesStore.loading || !devicesStore.updated || (devicesStore.updated && devicesStore.devices.length == 0)) return;
    if (licenseTimeout.current) clearTimeout(licenseTimeout.current);

    if (
      licenseStore.loading ||
      isNaN(licenseStore.expiration) ||
      licenseStore.expiration > 90
    ) return;

    const lastSawLicenseInterrupt = localStore.lastSawLicenseInterrupt;
    if (
      licenseStore.expiration > 30 &&
      (
        !lastSawLicenseInterrupt ||
        Date.now() - lastSawLicenseInterrupt > 24 * 60 * 60 * 1000
      )
    ) {
      // Spawn the modal and kick off appropriate intervals
      openLicenseExpirationModal();
    } else if (licenseStore.expiration <= 30) {
      // Spawn the modal and kick off appropriate intervals
      openLicenseExpirationModal();
    }

    // Add the global banner
    notificationsStoreActions.setGlobalNotification(
      i18n._(msg`Thermon Egis license expires in ${licenseStore.expiration} days. Please contact Thermon to renew.`),
      'warning',
      (licenseStore.expiration < 16 ? true : false)
    );

    return () => {
      // Clear the timeout when the component unmounts
      if (licenseTimeout.current) clearTimeout(licenseTimeout.current);
    }
  }, [licenseStore]);

  // NOTE - this is a hacky way to make sure the license useEffect only runs once on initial load.
  const devicesInitialLoad = useRef(false);
  useEffect(() => {
    if (devicesInitialLoad.current || !devicesStore.updated) return;
    licenseStoreActions.forceUpdate();
    devicesInitialLoad.current = true;
  }, [devicesStore.updated]);

  /*
    Handle connectivity issues

    NOTE - this seems a little goofy (and it is) but because of how
    react works, the closure instance of notificationStore seen by
    these event handlers is the initial empty state. We could watch
    for notificationStore in the main useEffect but then we'll be
    constantly adding and removing the listeners. So, instead we've
    created a ref that can be updated with notification store while
    the main offline handler logic only runs once.
  */
  const globalNotification = useRef(notificationsStore.globalNotification);

  useEffect(() => {
    globalNotification.current = notificationsStore.globalNotification;
  }, [notificationsStore]);

  useEffect(() => {
    const offlineMessage = msg`Your browser has lost connection to the network. Some functionality may be unavailable.`
    function handleOffline() {
      notificationsStoreActions.setGlobalNotification(i18n._(offlineMessage), 'warning');
    }

    function handleOnline(){
      if (globalNotification?.current?.msg == i18n._(offlineMessage)) {
        notificationsStoreActions.clearGlobalNotification();
      }
    }

    window.addEventListener('offline', handleOffline);
    window.addEventListener('online', handleOnline);

    return () => {
      window.removeEventListener('offline', handleOffline);
      window.removeEventListener('online', handleOnline);
    }
  }, []);
  /* / */

  /*
   * Outlet is where React Router outputs view components
   * described by the route.
   *
   * [<Outlet />](https://reactrouter.com/en/main/components/outlet)
   */
  return (
    <I18nProvider i18n={i18n}>
      {!authenticated ?
        <Login />
        :
        <>
          <GlobalBanner />
          <header>
            <h1><Link to="/">Thermon <span>Egis</span></Link></h1>
            <Navigation routes={routes} />
          </header>
          <section>
            { location.pathname == '/' ? <Dashboard /> : <Outlet /> }
          </section>
        </>
      }
    </I18nProvider>
  )
}

export default App;
